import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApprovalDecision, TRIP_TYPE_LABELS, TripType } from '@fleet/shared';
import { bookingsApi, type PendingBooking } from '../api/bookings';
import { formatDay, formatRange } from '../calendar/calendar-geometry';
import { AsyncBoundary } from '../ui/AsyncBoundary';
import { Badge, ColorDot } from '../ui/Badge';
import { Button } from '../ui/Button';
import { ErrorNotice } from '../ui/ErrorNotice';
import { Modal } from '../ui/Modal';
import { Field, inputClass } from '../ui/Field';

export function ApprovalInbox() {
  const [rejecting, setRejecting] = useState<PendingBooking | null>(null);
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ['pending-approvals'], queryFn: bookingsApi.pending });

  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
      comment,
    }: {
      id: string;
      decision: ApprovalDecision;
      comment?: string;
    }) => bookingsApi.decide(id, decision, comment),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      // Eine Ablehnung gibt den Zeitslot wieder frei.
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
      await queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      setRejecting(null);
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">Freigaben</h2>
        <p className="text-sm text-slate-500">
          Buchungen, die auf eine Entscheidung warten. Sie belegen den Zeitraum bereits — eine
          Ablehnung gibt ihn wieder frei.
        </p>
      </div>

      {decide.isError && <ErrorNotice error={decide.error} />}

      <AsyncBoundary query={query} empty="Nichts zu entscheiden.">
        {(bookings) => (
          <ul className="space-y-3">
            {bookings.map((booking) => (
              <PendingRow
                key={booking.id}
                booking={booking}
                isBusy={decide.isPending && decide.variables?.id === booking.id}
                onApprove={() =>
                  decide.mutate({ id: booking.id, decision: ApprovalDecision.APPROVED })
                }
                onReject={() => setRejecting(booking)}
              />
            ))}
          </ul>
        )}
      </AsyncBoundary>

      {rejecting && (
        <RejectDialog
          booking={rejecting}
          isBusy={decide.isPending}
          error={decide.error}
          onClose={() => setRejecting(null)}
          onConfirm={(comment) =>
            decide.mutate({ id: rejecting.id, decision: ApprovalDecision.REJECTED, comment })
          }
        />
      )}
    </div>
  );
}

function PendingRow({
  booking,
  isBusy,
  onApprove,
  onReject,
}: {
  booking: PendingBooking;
  isBusy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const start = new Date(booking.startsAt);
  const end = new Date(booking.endsAt);
  const isPast = end.getTime() < Date.now();

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <ColorDot color={booking.vehicle.colorHex} />
            <span className="font-medium">{booking.vehicle.licensePlate}</span>
            <span className="text-sm text-slate-500">{booking.vehicle.displayName}</span>
            <Badge tone={booking.tripType === TripType.PRIVAT ? 'amber' : 'neutral'}>
              {TRIP_TYPE_LABELS[booking.tripType]}
            </Badge>
            {booking.isOwn && <Badge tone="blue">Eigene Buchung</Badge>}
            {isPast && <Badge tone="red">Zeitraum verstrichen</Badge>}
          </div>

          <div className="text-sm">
            {formatRange(start, end)}
            <span className="text-slate-500"> · {booking.driver.displayName}</span>
          </div>

          <dl className="space-y-0.5 text-sm text-slate-600">
            {booking.purpose && <Detail label="Zweck" value={booking.purpose} />}
            {booking.destinationAddress && (
              <Detail label="Ziel" value={booking.destinationAddress} />
            )}
            {booking.passengers && <Detail label="Mitfahrende" value={booking.passengers} />}
            {booking.notes && <Detail label="Notiz" value={booking.notes} />}
          </dl>

          <div className="text-xs text-slate-400">
            {booking.bookingNumber} · {booking.vehicle.vehicleGroupName} · beantragt am{' '}
            {formatDay(new Date(booking.requestedAt))}
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
          <Button variant="danger" onClick={onReject} disabled={isBusy}>
            Ablehnen
          </Button>
          <Button variant="primary" onClick={onApprove} disabled={isBusy}>
            {isBusy ? 'Einen Moment …' : 'Freigeben'}
          </Button>
        </div>
      </div>

      {isPast && (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Der Zeitraum liegt in der Vergangenheit. Eine Freigabe ist weiterhin möglich, ändert aber
          nichts mehr an der Verfügbarkeit — eine Ablehnung gibt den Slot rückwirkend frei.
        </p>
      )}
    </li>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-slate-400">{label}:</dt>
      <dd>{value}</dd>
    </div>
  );
}

/**
 * Ablehnen mit Begründung.
 *
 * Der Grund ist Pflicht — sonst erfährt der Antragsteller nur, *dass* abgelehnt
 * wurde, und muss nachfragen, um zu wissen, was er ändern müsste.
 */
function RejectDialog({
  booking,
  isBusy,
  error,
  onClose,
  onConfirm,
}: {
  booking: PendingBooking;
  isBusy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: (comment: string) => void;
}) {
  const [comment, setComment] = useState('');
  const tooShort = comment.trim().length === 0;

  return (
    <Modal
      title={`${booking.vehicle.licensePlate} ablehnen`}
      description={`${booking.driver.displayName} · ${booking.bookingNumber}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <Field label="Begründung" hint="Sie erscheint beim Antragsteller unter „Meine Buchungen“.">
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={3}
            maxLength={500}
            className={inputClass}
            autoFocus
          />
        </Field>

        {error != null && <ErrorNotice error={error} />}

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="danger"
            disabled={tooShort || isBusy}
            onClick={() => onConfirm(comment.trim())}
          >
            {isBusy ? 'Wird gespeichert …' : 'Ablehnen'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
